import { compact } from 'lodash';

import { DiagramDispositionsFormRecipient } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { RecipientKind } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { calculateResiduaryRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import {
  AfterDeath,
  ClientOrganizationKind,
  DispositiveProvisionDispositionKind,
  DispositiveProvisionRecipientKind,
  DispositiveProvisionTransferTaxKind,
  EntityInEstateStatus,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { BdgMissingBeneficiaryTier } from '../bdgActiveTier';
import { GetDispositionsVizFromEntityInput } from '../getDispositionsVizFromEntity';
import {
  DispositionsDiagram_DispositionProvisionFirstDeathFragment,
  DispositionsDiagram_DispositionProvisionSecondDeathFragment,
  DispositionsDiagram_DispositionProvisionTerminalFragment,
} from '../graphql/DispositionsDiagram.generated';
import {
  EntityDiagram_EdgeFragment,
  EntityDiagram_NodeFragment,
  EntityDiagramEdgeKind,
} from '../types';
import { makeNodeFromRecipient } from './nodes';
import {
  getNotFullyDistributedWarning,
  getOverDistributedWarning,
  getSpecifyContingentBeneficiaryWarning,
  getSpecifyPrimaryBeneficiaryWarning,
  getWillNotReceiveDispositionsWarning,
  MultipleWarningIndicators,
  WarningData,
} from './warnings';

/**
 * Convert GraphQL recipient kind to form recipient kind
 */
function convertRecipientKind(
  graphqlRecipientKind: unknown
): RecipientKind | null {
  switch (graphqlRecipientKind) {
    case 'INDIVIDUAL':
      return RecipientKind.Individual;
    case 'ENTITY':
      return RecipientKind.Entity;
    case 'ORGANIZATION':
      return RecipientKind.Organization;
    case 'TESTAMENTARY_ENTITY':
      return RecipientKind.TestamentaryEntity;
    case 'SURVIVING_SPOUSE':
      return RecipientKind.SurvivingSpouse;
    default:
      return null;
  }
}

export function makeDiagramDispositionsFormRecipientFromFragment(
  d:
    | DispositionsDiagram_DispositionProvisionTerminalFragment
    | DispositionsDiagram_DispositionProvisionSecondDeathFragment
    | DispositionsDiagram_DispositionProvisionFirstDeathFragment,
  survivingSpouse: { id: string; displayName: string },
  firstGrantorDeathId: string
): DiagramDispositionsFormRecipient {
  // Extract display name and other properties based on recipient type
  let displayName = '';
  let isCharitableOrganization = false;

  if (d.organization) {
    displayName = d.organization?.name || '';
    isCharitableOrganization =
      d.organization?.clientOrganizationKind ===
      ClientOrganizationKind.CharitableOrganization;
  } else if (d.individual) {
    displayName = d.individual.displayName || '';
  } else if (d.entity) {
    displayName = d.entity.subtype?.displayName || '';
  } else if (d.testamentaryEntity) {
    displayName = d.testamentaryEntity.displayName || '';
  }

  let recipientSurvivingSpouseId: string | null = null;
  let estateStatus = EntityInEstateStatus.OutOfEstate;

  if (d.recipientKind === DispositiveProvisionRecipientKind.SurvivingSpouse) {
    displayName = survivingSpouse?.displayName || '';
    recipientSurvivingSpouseId = survivingSpouse?.id || null;
    estateStatus = EntityInEstateStatus.InEstate;
  }

  if (
    d.individual &&
    (d.individual.id === firstGrantorDeathId ||
      d.individual.id === survivingSpouse?.id)
  ) {
    estateStatus = EntityInEstateStatus.InEstate;
  }

  if (
    d.individual &&
    d.individual.id !== firstGrantorDeathId &&
    d.individual.id !== survivingSpouse?.id
  ) {
    estateStatus = EntityInEstateStatus.OutOfEstate;
  }

  if (d.entity && 'inEstateStatus' in d.entity.subtype) {
    estateStatus = d.entity.subtype.inEstateStatus;
  }

  if (d.testamentaryEntity && 'inEstateStatus' in d.testamentaryEntity) {
    estateStatus = d.testamentaryEntity.inEstateStatus;
  }

  return {
    displayName,
    entityKind: d.entity?.entityKind ?? undefined,
    testamentaryEntityKind:
      d.testamentaryEntity?.testamentaryEntityKind ?? undefined,
    inEstateStatus: estateStatus,
    recipientId:
      d.individual?.id ||
      d.entity?.id ||
      d.testamentaryEntity?.id ||
      d.organization?.id ||
      recipientSurvivingSpouseId ||
      null,
    subtypeId: d.entity?.subtype.id || undefined,
    recipientKind: convertRecipientKind(d.recipientKind),
    isCharitableOrganization,
    dispositionKind_ButtonGroup:
      d.dispositionKind === DispositiveProvisionDispositionKind.AnythingLeftOver
        ? 'other'
        : d.dispositionKind,
    dispositionKind_Select: d.dispositionKind,
    dispositionAmount: d.dispositionAmount ?? null,
    dispositionPercentage: d.dispositionPercentage ?? null,
    dispositionFormulaicPercentage: d.dispositionFormulaicPercentage ?? null,
    notes: '',
    transferTaxKind: d.transferTaxKind || '',
    _dispositiveProvisionId: null,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
  };
}

export function makeDiagramDispositionsFormRecipientFromEntity(
  entity: GetDispositionsVizFromEntityInput['entity']
): DiagramDispositionsFormRecipient {
  // Determine recipient kind from entity ID using PULID
  const pulidKind = getPulidKind(entity.id);
  let recipientKind: RecipientKind;

  switch (pulidKind) {
    case PulidKind.Entity:
      recipientKind = RecipientKind.Entity;
      break;
    case PulidKind.TestamentaryEntity:
      recipientKind = RecipientKind.TestamentaryEntity;
      break;
    case PulidKind.ClientProfile:
      recipientKind = RecipientKind.Individual;
      break;
    case PulidKind.ClientOrganization:
      recipientKind = RecipientKind.Organization;
      break;
    default:
      // Default to Entity if we can't determine the kind
      recipientKind = RecipientKind.Entity;
  }

  return {
    displayName: entity.subtype.displayName,
    recipientId: entity.id,
    subtypeId: entity.subtype.id,
    recipientKind,
    entityKind: entity.entityKind,
    inEstateStatus: entity.subtype.inEstateStatus ?? undefined,
    isCharitableOrganization: false, // Entities are not charitable organizations by default
    // Default disposition properties - these will be overridden by actual dispositions
    dispositionKind_ButtonGroup: 'other',
    dispositionKind_Select: '',
    dispositionAmount: null,
    dispositionPercentage: null,
    dispositionFormulaicPercentage: null,
    notes: '',
    transferTaxKind: '',
    _dispositiveProvisionId: null,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
  };
}

/**
 * Get next death section
 */
function getNextSection(current: AfterDeath): AfterDeath {
  switch (current) {
    case AfterDeath.None:
      return AfterDeath.First;
    case AfterDeath.First:
      return AfterDeath.Second;
    case AfterDeath.Second:
      return AfterDeath.Second; // Final section
    default:
      throw new UnreachableError(current);
  }
}

/**
 * Determine if entity should cascade to next section
 */
function shouldCascadeEntity(
  context: EntityProcessingContext,
  isFullyDistributedOnFirstDeath: boolean,
  isFullyDistributed: boolean,
  isTwoClientHousehold: boolean
): boolean {
  const { dispositions, afterDeath } = context;

  if (context.entity.node?.__typename === 'ClientOrganization') {
    return false;
  }

  if (afterDeath === AfterDeath.First && !isTwoClientHousehold) {
    return false;
  }

  // A pour-over recipient starts its own provision sequence even when it
  // receives assets in a later diagram section.
  if (
    dispositions.secondDeath.length > 0 &&
    (afterDeath === AfterDeath.None ||
      context.dispositionPhase === 'firstDeath')
  ) {
    return true;
  }

  // If fully distributed on first death, do not cascade from First to Second
  if (isFullyDistributedOnFirstDeath && afterDeath === AfterDeath.First) {
    return false;
  }

  // Always cascade if not fully distributed (even with no dispositions)
  if (!isFullyDistributed) {
    return true;
  }

  return false;
}

export interface NormalizedDispositions {
  firstDeath: DiagramDispositionsFormRecipient[];
  secondDeath: DiagramDispositionsFormRecipient[];
  /**
   * Set for a BDG (retirement / insurance) entity whose active beneficiary
   * tier resolved empty. Re-messages that entity's not-fully-distributed
   * warning as "Please specify a {tier} beneficiary".
   */
  missingBeneficiaryTier?: BdgMissingBeneficiaryTier;
  /** Saved dispositions that are intentionally outside this diagram's depth. */
  hasUnrenderedDispositions?: boolean;
}

interface EntityProcessingContext {
  entity: EntityDiagram_NodeFragment;
  parentEntity?: EntityDiagram_NodeFragment;
  dispositions: NormalizedDispositions;
  afterDeath: AfterDeath;
  receivedViaPourover?: boolean;
  isCascade?: boolean;
  dispositionPhase?: 'firstDeath' | 'secondDeath';
  originalRootDispositions?: NormalizedDispositions; // Store original root dispositions for cascading
}

export function processEntityDispositions(
  context: EntityProcessingContext,
  firstGrantorDeathId: string,
  isTwoClientHousehold: boolean,
  survivingSpouse: { id: string; displayName: string } | undefined,
  isOnDispositionsFormDiagram: boolean,
  nodes: EntityDiagram_NodeFragment[],
  edges: EntityDiagram_EdgeFragment[],
  allDispositions: Record<string, NormalizedDispositions>,
  willNotReceiveDispositions: boolean,
  // Track visited entity+section combinations to detect cycles (e.g., A → B → A via pourovers)
  visited: Set<string> = new Set()
): void {
  const { entity, dispositions, afterDeath } = context;
  const startsOwnSequence =
    context.receivedViaPourover && dispositions.firstDeath.length > 0;
  const dispositionPhase =
    context.dispositionPhase ??
    (afterDeath === AfterDeath.None || startsOwnSequence
      ? 'firstDeath'
      : 'secondDeath');
  // A final-section recipient can continue a pour-over chain. A retained
  // balance must not repeat provisions that ran in the previous section.
  const canDistributeAfterSecondDeath =
    !context.isCascade &&
    (context.receivedViaPourover ||
      dispositions.secondDeath.some(
        (disposition) =>
          disposition.transferTaxKind ===
          DispositiveProvisionTransferTaxKind.Pourover
      ));

  // Create a unique key for this entity in this death section
  const visitKey = `${entity.id}:${afterDeath}:${dispositionPhase}:${afterDeath === AfterDeath.Second && canDistributeAfterSecondDeath ? 'distributing' : 'terminal'}`;

  // Cycle detection: if we've already processed this entity in this section, skip to avoid infinite recursion
  if (visited.has(visitKey)) {
    return;
  }
  visited.add(visitKey);

  // The dispositions we want to process and link with the context node
  const currentDispositions = (() => {
    if (
      afterDeath === AfterDeath.Second &&
      !canDistributeAfterSecondDeath &&
      context.dispositionPhase !== 'secondDeath'
    )
      return [];
    return dispositionPhase === 'firstDeath'
      ? dispositions.firstDeath
      : dispositions.secondDeath;
  })();

  // Create recipient node collection
  const recipientNodes: EntityDiagram_NodeFragment[] = [];

  const { isFullyDistributed: isEverFullyDistributed } =
    calculateResiduaryRecipients([
      ...dispositions.firstDeath,
      ...dispositions.secondDeath,
    ]);
  const { isFullyDistributed, exceeds100Percent, residuaryIndex } =
    calculateResiduaryRecipients(currentDispositions);
  const { isFullyDistributed: isFullyDistributedOnFirstDeath } =
    calculateResiduaryRecipients(dispositions.firstDeath);

  // Check if this context has any pourover dispositions - ordering logic only applies to PO diagrams
  const hasPouroverDispositions = currentDispositions.some(
    (d) => d.transferTaxKind === DispositiveProvisionTransferTaxKind.Pourover
  );

  // Keep each same-section recipient below its source, including nested hops.
  let pouroverRecipientOrder =
    ('order' in entity ? (entity.order ?? 0) : 0) + 1;

  currentDispositions.forEach((disposition, idx) => {
    const hasIncomingPouroverDisposition =
      disposition.transferTaxKind ===
      DispositiveProvisionTransferTaxKind.Pourover;

    // Pour-over recipients stay in the same section (above death line), matching waterfall behavior
    const recipientSection = hasIncomingPouroverDisposition
      ? afterDeath
      : getNextSection(afterDeath);

    const recipientNode = makeNodeFromRecipient({
      recipient: disposition,
      afterDeath: recipientSection,
      survivingSpouse,
      firstGrantorDeathId,
      hasIncomingPouroverDisposition,
    });

    if (recipientNode) {
      // The final death section also stacks ordinary gifts below their source,
      // since there is no later section to receive them.
      if (recipientSection === afterDeath && recipientNode.node) {
        const tileNode = recipientNode as EntityDiagram_NodeFragment & {
          order?: number;
          stackY?: boolean;
          yOffset?: number;
        };
        tileNode.order = pouroverRecipientOrder++; // Unique order for each pourover recipient
        tileNode.stackY = true; // Stack vertically below source
        tileNode.yOffset = 30; // Add extra vertical spacing
      } else if (hasPouroverDispositions && recipientNode.node) {
        // Non-pourover recipients (e.g., spousal, charity) get a high order
        // to place them after cascaded pourover entities in subsequent sections
        // Only applies to diagrams with pourover dispositions
        const tileNode = recipientNode as EntityDiagram_NodeFragment & {
          order?: number;
        };
        tileNode.order = 1000 + idx;
      }
      recipientNodes.push(recipientNode);
    } else {
      diagnostics.warn(
        `No recipient node found for disposition ${disposition.displayName}`
      );

      return;
    }

    // Process recipient's dispositions
    processEntityDispositions(
      {
        entity: {
          ...recipientNode,
          ...(recipientNode.warningIndicator && {
            warningIndicator: recipientNode.warningIndicator,
          }),
        },
        parentEntity: {
          ...entity,
        },
        dispositions: allDispositions[recipientNode.id] ?? {
          firstDeath: [],
          secondDeath: [],
        },
        afterDeath: recipientSection, // Process dispositions from recipient's section, not parent's section
        receivedViaPourover: hasIncomingPouroverDisposition,
        originalRootDispositions: context.originalRootDispositions,
      },
      firstGrantorDeathId,
      isTwoClientHousehold,
      survivingSpouse,
      isOnDispositionsFormDiagram,
      nodes,
      edges,
      allDispositions,
      idx > (residuaryIndex ?? Infinity) ||
        (recipientSection === AfterDeath.Second &&
          dispositionPhase === 'secondDeath' &&
          isFullyDistributedOnFirstDeath),
      visited
    );
  });

  // Collect warnings for this node
  const recipientWarnings: WarningData[] = [];

  if (exceeds100Percent) {
    recipientWarnings.push(getOverDistributedWarning());
  }

  const shouldShowNotFullyDistributedWarning = (() => {
    if (
      isEverFullyDistributed ||
      dispositions.hasUnrenderedDispositions ||
      afterDeath === AfterDeath.Second ||
      (!isTwoClientHousehold && afterDeath === AfterDeath.First) ||
      entity.node?.__typename === 'ClientOrganization'
    ) {
      return false;
    }

    const spouses = compact([survivingSpouse?.id, firstGrantorDeathId]);

    if (
      entity.node?.__typename === 'ClientProfile' &&
      !spouses.includes(entity.id)
    ) {
      return false;
    }

    return true;
  })();

  if (shouldShowNotFullyDistributedWarning) {
    const nodeInNone = nodes.find(
      (n) => n.id === entity.id && n.afterDeath === AfterDeath.None
    );
    if (afterDeath === AfterDeath.First && nodeInNone) {
      // do not add the warning as it will be on the node in the None section
    } else if (dispositions.missingBeneficiaryTier === 'primary') {
      recipientWarnings.push(getSpecifyPrimaryBeneficiaryWarning());
    } else if (dispositions.missingBeneficiaryTier === 'contingent') {
      recipientWarnings.push(getSpecifyContingentBeneficiaryWarning());
    } else {
      recipientWarnings.push(getNotFullyDistributedWarning(entity));
    }
  }

  if (willNotReceiveDispositions && context.parentEntity) {
    recipientWarnings.push(
      getWillNotReceiveDispositionsWarning(context.parentEntity)
    );
  }

  if (recipientWarnings.length > 0 && isOnDispositionsFormDiagram) {
    entity.warningIndicator = (
      <MultipleWarningIndicators warnings={recipientWarnings} />
    );
  }

  if (
    entity.node?.__typename === 'Entity' ||
    entity.node?.__typename === 'TestamentaryEntity' ||
    entity.node?.__typename === 'ClientProfile'
  ) {
    entity.node.hasOutgoingPouroverDisposition = hasPouroverDispositions;
    // Source entity with pour-over should stack vertically with recipient below.
    if (
      hasPouroverDispositions ||
      (afterDeath === AfterDeath.Second && recipientNodes.length > 0)
    ) {
      const tileEntity = entity as EntityDiagram_NodeFragment & {
        order?: number;
        stackY?: boolean;
      };
      tileEntity.order ??= 0;
      tileEntity.stackY = true;
    }
  }

  // Add the current node to the nodes array
  nodes.push(entity);

  // Create edges from entity to recipients
  if (recipientNodes.length > 0) {
    recipientNodes.forEach((recipientNode) => {
      const edge = {
        from: entity,
        to: recipientNode,
        kind: EntityDiagramEdgeKind.Disposition,
      };
      edges.push(edge);
    });
  }

  // The last section has no further death boundary. Continue the recipient's
  // remaining phase here without creating a self-edge or repeating phase one.
  if (
    afterDeath === AfterDeath.Second &&
    dispositionPhase === 'firstDeath' &&
    dispositions.secondDeath.length > 0
  ) {
    processEntityDispositions(
      {
        ...context,
        dispositionPhase: 'secondDeath',
        isCascade: true,
        receivedViaPourover: false,
      },
      firstGrantorDeathId,
      isTwoClientHousehold,
      survivingSpouse,
      isOnDispositionsFormDiagram,
      nodes,
      edges,
      allDispositions,
      willNotReceiveDispositions,
      visited
    );
    if (entity.node && 'hasOutgoingPouroverDisposition' in entity.node) {
      entity.node.hasOutgoingPouroverDisposition ||= hasPouroverDispositions;
    }
  }

  // Cascade entity if not fully distributed
  const shouldCascade = shouldCascadeEntity(
    { ...context, dispositionPhase },
    isFullyDistributedOnFirstDeath,
    isFullyDistributed,
    isTwoClientHousehold
  );
  if (afterDeath !== AfterDeath.Second && shouldCascade) {
    const nextSection = getNextSection(afterDeath);

    // Destructure to remove pourover-specific stacking properties that shouldn't cascade
    // stackY and yOffset control vertical stacking for pourovers - these are section-specific
    // We keep `order` as it helps maintain consistent left-to-right positioning across sections
    const {
      stackY: _stackY,
      yOffset: _yOffset,
      ...entityWithoutPouroverStacking
    } = entity as EntityDiagram_NodeFragment & {
      stackY?: boolean;
      yOffset?: number;
    };

    let entityNode = {};
    if (entity.node) {
      entityNode = {
        node: {
          ...entity.node,
          // Reset pourover-specific flag - will be recalculated for the new section
          hasOutgoingPouroverDisposition: false,
        },
      };
    }

    processEntityDispositions(
      {
        ...context,
        entity: {
          ...entityWithoutPouroverStacking,
          afterDeath: nextSection,
          warningIndicator: undefined,
          ...entityNode,
        },
        afterDeath: nextSection,
        isCascade: true,
        receivedViaPourover: false,
        dispositionPhase:
          dispositionPhase === 'firstDeath' ? 'secondDeath' : undefined,
      },
      firstGrantorDeathId,
      isTwoClientHousehold,
      survivingSpouse,
      isOnDispositionsFormDiagram,
      nodes,
      edges,
      allDispositions,
      false,
      visited
    );

    // Create the cascaded entity node AFTER processing recipients
    // Use the same cleaned properties as the recursive call for consistency
    const cascadedEntityNode = {
      ...entityWithoutPouroverStacking,
      afterDeath: nextSection,
      ...entityNode,
    };

    // Add cascading edge
    const cascadeEdge = {
      from: entity,
      to: cascadedEntityNode,
      kind: EntityDiagramEdgeKind.Disposition,
    };

    if (nextSection !== AfterDeath.Second) {
      edges.push(cascadeEdge);
    }

    if (nextSection === AfterDeath.Second && isTwoClientHousehold) {
      edges.push(cascadeEdge);
    }
  }
}
